// src/context/AuthContext.js

import React, { createContext, useReducer, useEffect } from "react";
import auth, { handleOAuthError } from "../services/auth";
import { jwtDecode } from "jwt-decode";

const IDENTITY_USER_ENDPOINT = "/.netlify/identity/user";
const ADD_USER_ENDPOINT = "/.netlify/functions/add-user";
const USER_STORAGE_KEY = "user";   // persisted user object
const JWT_STORAGE_KEY = "token";  // Twitter/Facebook JWT

/**
 * Load initial user:
 * 1) Try custom JWT (Twitter/Facebook) from localStorage.token
 * 2) Try saved Netlify‐Identity user from localStorage.user
 * 3) Otherwise null—will wait for GoTrue init to rehydrate session
 */
function normalizeUserForDb(user) {
	const id = user.id || user.provider_id;
	const email = user.email || "";
	const fullName =
		user.user_metadata?.full_name ||
		user.name ||
		user.username ||
		"";
	return { id, email, fullName };
}
function getInitialUser() {
	if (typeof window === "undefined") return null;

	// 1) Custom JWT
	const rawJwt = localStorage.getItem(JWT_STORAGE_KEY);
	if (rawJwt) {
		try {
			return jwtDecode(rawJwt);
		} catch {
			localStorage.removeItem(JWT_STORAGE_KEY);
		}
	}

	// 2) Persisted Identity user
	const rawUser = localStorage.getItem(USER_STORAGE_KEY);
	if (rawUser) {
		try {
			return JSON.parse(rawUser);
		} catch {
			localStorage.removeItem(USER_STORAGE_KEY);
		}
	}

	// 3) No initial user
	return null;
}

const rawInitial = getInitialUser();
const initialState = {
	user:
		rawInitial?.provider === "twitter"
			? normalizeUserForDb(rawInitial)
			: rawInitial,
};

const authReducer = (state, action) => {
	switch (action.type) {
		case "LOGIN":
			if (typeof window !== "undefined") {
				localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(action.payload));
			}
			return { ...state, user: action.payload };

		case "LOGOUT":
			if (typeof window !== "undefined") {
				localStorage.removeItem(USER_STORAGE_KEY);
				localStorage.removeItem(JWT_STORAGE_KEY);
			}
			return { ...state, user: null };

		default:
			return state;
	}
};

export const AuthContext = createContext({
	user: null,
	loginUser: () => { },
	logoutUser: () => { },
});

export const AuthProvider = ({ children }) => {
	const [state, dispatch] = useReducer(authReducer, initialState);

	const loginUser = (userData) => dispatch({ type: "LOGIN", payload: userData });
	const logoutUser = () =>
		auth.logout().finally(() => dispatch({ type: "LOGOUT" }));

	// 1) Catch Netlify Identity OAuth errors (Google etc.)
	useEffect(() => {
		try {
			handleOAuthError();
		} catch (err) {
			console.error("OAuth error:", err);
		}
	}, []);

	// 2) Subscribe to GoTrue init to rehydrate Netlify Identity session from cookie
	useEffect(() => {
		if (typeof window === "undefined") return;
		// console.log("auth", auth)
		auth.on("init", () => {
			const u = auth.currentUser();
			if (u) loginUser(u);
		});

		// auth.on is provided by gotrue-js to signal when it has restored a session
		// auth.on("init", () => {
		// 	const current = auth.currentUser();
		// 	if (current) {
		// 		loginUser(current);
		// 		// Mirror to backend
		// 		fetch(ADD_USER_ENDPOINT, {
		// 			method: "POST",
		// 			headers: { "Content-Type": "application/json" },
		// 			body: JSON.stringify({ user: current, provider: "identity" }),
		// 		}).catch(console.error);
		// 	}
		// });
	}, []);
	//Hydrate from custom JWT on app load(Twitter / Facebook)
	useEffect(() => {
		const appJwt = localStorage.getItem(JWT_STORAGE_KEY);
		if (appJwt) {
			try {
				const user = jwtDecode(appJwt);
				const normalized = normalizeUserForDb(user);
				loginUser(normalized);
			} catch {
				localStorage.removeItem(JWT_STORAGE_KEY);
			}
		}
	}, []);

	// 3) Handle Netlify Identity redirects (#access_token flows for Google, GitHub, etc.)
	useEffect(() => {
		if (typeof window === "undefined") return;

		// Combine hash and search params
		const merged = window.location.hash.replace(/^#/, "") +
			"&" +
			window.location.search.replace(/^\?/, "");
		const params = new URLSearchParams(merged);
		const token = params.get("access_token");
		const provider = params.get("provider") || "identity";

		if (!token) return;

		// Clean URL: remove access_token and provider from address bar
		const cleanSearch = window.location.search
			.replace(/(\?|&)provider=[^&]+/, "")
			.replace(/(\?|#)access_token=[^&]+/, "");
		window.history.replaceState(null, "", window.location.pathname + cleanSearch);

		// Only fetch /identity/user for built-in flows
		if (provider === "identity") {
			fetch(IDENTITY_USER_ENDPOINT, {
				headers: { Authorization: `Bearer ${token}` },
			})
				.then(res => {
					if (!res.ok) throw new Error("Failed to fetch Netlify user");
					return res.json();
				})
				.then(user => {
					loginUser(user);
					// Mirror to backend
					fetch(ADD_USER_ENDPOINT, {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ user, provider }),
					}).catch(console.error);
				})
				.catch(err => console.error("Identity redirect handling failed:", err));
		}
	}, []);

	// 4) Mirror every login (fallback) to backend when state.user changes
	useEffect(() => {
		if (!state.user) return;
		const payload =
			state.user.provider === "twitter"
				? normalizeUserForDb(state.user)
				: state.user;
		fetch(ADD_USER_ENDPOINT, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ user: payload }),
		}).catch(err =>
			console.error("Error syncing user on state change:", err)
		);
	}, [state.user]);

	return (
		<AuthContext.Provider value={{ user: state.user, loginUser, logoutUser }}>
			{children}
		</AuthContext.Provider>
	);
};
